import { useEffect, useState, useCallback, useRef } from 'react';
import { Tab, Tabs, TabList, TabPanel } from 'react-tabs';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faMagnifyingGlass,
    faImage,
    faVideo,
    faPodcast,
    faPlus,
    faSpinner,
    faRotate,
    faBroadcastTower,
} from '@fortawesome/free-solid-svg-icons';
import 'react-tabs/style/react-tabs.css';
import './GalleryManagement.css';

import GalleryDetail from './GalleryDetail';
import NewGalleryForm from './NewGallery';
import MediaCentreManagement from './MediaCentreManagement';
import { galleryService } from '../../services/galleryService';
import { getMediaCentreItems } from '../../services/mediaService';
import { toast } from 'react-toastify';
import { flushSync } from 'react-dom';
import Tippy from '@tippyjs/react';

function GalleryManagement({ currentUser }) {
    // Gallery state
    const [galleries, setGalleries] = useState([]);
    const [isLoadingGalleries, setIsLoadingGalleries] = useState(false);
    const fetchInProgressRef = useRef(false);
    const initialFetchDoneRef = useRef(false);

    // Gallery UI state
    const [gallerySearchTerm, setGallerySearchTerm] = useState('');
    const [selectedGalleryTab, setSelectedGalleryTab] = useState(0);
    const [selectedGallery, setSelectedGallery] = useState(null);
    const [showNewGalleryForm, setShowNewGalleryForm] = useState(false);

    // Gallery items state
    const [galleryItems, setGalleryItems] = useState([]);
    const [isLoadingItems, setIsLoadingItems] = useState(false);

    // Thumbnail state
    const [galleryThumbnails, setGalleryThumbnails] = useState({});
    const [loadingThumbnails, setLoadingThumbnails] = useState({});

    // Refresh state
    const [isRefreshingGalleries, setIsRefreshingGalleries] = useState(false);

    // Media Centre state
    const [mediaCentreItemCount, setMediaCentreItemCount] = useState(0);

    // Fetch galleries function
    const fetchGalleries = useCallback(async (forceRefresh = true) => {
        try {
            setIsLoadingGalleries(true);

            // Force a fresh fetch by clearing cache first
            galleryService.clearCache();
            const freshGalleries = await galleryService.getGalleries(forceRefresh);
            setGalleries(freshGalleries);
        } catch (error) {
            console.error('Failed to fetch galleries:', error);
            toast.error('Failed to refresh galleries');
        } finally {
            setIsLoadingGalleries(false);
        }
    }, []);

    // Fetch Media Centre item count
    const fetchMediaCentreCount = useCallback(async () => {
        try {
            const items = await getMediaCentreItems();
            setMediaCentreItemCount(items?.length || 0);
        } catch (error) {
            console.error('Failed to fetch Media Centre count:', error);
            // Silent fail for count - don't show error to user
        }
    }, []);

    // Initial fetch effect
    useEffect(() => {
        if (!initialFetchDoneRef.current) {
            initialFetchDoneRef.current = true;
            fetchGalleries();
            fetchMediaCentreCount();
        }
    }, [fetchGalleries, fetchMediaCentreCount]);

    // Filter galleries by type
    const getFilteredGalleriesByType = (type) => {
        if (!galleries || !Array.isArray(galleries)) {
            return [];
        }

        return galleries.filter(gallery => {
            if (!gallery) return false;

            const matchesSearch = gallery.title?.toLowerCase().includes(gallerySearchTerm.toLowerCase()) ||
                gallery.description?.toLowerCase().includes(gallerySearchTerm.toLowerCase());

            const typeMatches = type === 'all' || gallery.type?.toLowerCase() === type.toLowerCase();

            return matchesSearch && typeMatches;
        });
    };

    // Effect to update selected tab based on search results
    useEffect(() => {
        if (gallerySearchTerm) {
            const imageCount = getFilteredGalleriesByType('image').length;
            const videoCount = getFilteredGalleriesByType('video').length;
            const podcastCount = getFilteredGalleriesByType('podcast').length;

            // Find the type with most matches
            const counts = [imageCount, videoCount, podcastCount];
            const maxCount = Math.max(...counts);
            const maxIndex = counts.indexOf(maxCount);

            setSelectedGalleryTab(maxIndex);
        }
    }, [gallerySearchTerm]);

    // Effect to fetch gallery items when a gallery is selected
    useEffect(() => {
        const fetchGalleryItems = async () => {
            if (!selectedGallery) return;

            setIsLoadingItems(true);
            try {
                const items = await galleryService.getGalleryMedia(selectedGallery.id);
                setGalleryItems(items || []);
            } catch (error) {
                console.error('Error fetching gallery items:', error);
                toast.error('Failed to load gallery items');
                setGalleryItems([]);
            } finally {
                setIsLoadingItems(false);
            }
        };

        fetchGalleryItems();
    }, [selectedGallery]);

    // Gallery click handler
    const handleGalleryClick = async (gallery) => {
        setSelectedGallery(gallery);

        // Fetch gallery items
        try {
            const items = await galleryService.getGalleryMedia(gallery.id);
            setGalleryItems(items || []);
        } catch (error) {
            console.error('Error fetching gallery items:', error);
            toast.error('Failed to load gallery items');
            setGalleryItems([]);
        }
    };

    // Gallery close handler
    const handleGalleryClose = async () => {
        setSelectedGallery(null);

        // Clear cache and refresh galleries to get latest thumbnails
        galleryService.clearCache();
        await fetchGalleries();
    };

    // Gallery save handler
    const handleGallerySave = useCallback((updatedGallery) => {
        setGalleries(prev => prev.map(g =>
            g.id === updatedGallery.id ? updatedGallery : g
        ));

        // Only fetch if we really need to
        if (updatedGallery.keepOpen && !fetchInProgressRef.current) {
            fetchGalleries();
        } else {
            setSelectedGallery(null);
        }
    }, [fetchGalleries]);

    // New gallery handlers
    const handleNewGallery = () => {
        setShowNewGalleryForm(true);
    };

    // Get current gallery type based on selected tab
    const getCurrentGalleryType = () => {
        const tabTypes = ['image', 'video', 'podcast'];
        return tabTypes[selectedGalleryTab] || 'image';
    };

    const handleNewGalleryClose = () => {
        setShowNewGalleryForm(false);
    };

    const handleNewGallerySave = async (newGallery) => {
        
        
        try {
            setIsLoadingGalleries(true);

            // Add the new gallery to the state immediately
            setGalleries(prevGalleries => {
                const updated = [...prevGalleries, newGallery];
               
                return updated;
            });

            // Close the form first
            setShowNewGalleryForm(false);

            // Switch to the appropriate tab
            const galleryType = newGallery.type?.toLowerCase() || newGallery.galleryType?.toLowerCase();
            const tabIndex = {
                'image': 0,
                'video': 1,
                'podcast': 2
            }[galleryType] || 0;

           
            setSelectedGalleryTab(tabIndex);

            toast.success('Gallery created successfully! Opening gallery...');

            // Auto-open the newly created gallery
            setTimeout(() => {
               
                handleGalleryClick(newGallery);
            }, 800);

            // Refresh galleries in the background to ensure consistency
            try {
                galleryService.clearCache();
                await fetchGalleries();
                
            } catch (refreshError) {
                console.warn('⚠️ Background refresh failed:', refreshError);
                // Don't show error to user since the main operation succeeded
            }

        } catch (error) {
            console.error('Error handling new gallery:', error);
            // Still close the form even if something fails
            setShowNewGalleryForm(false);
            toast.error('Gallery created but there was an issue. Please refresh the page.');
        } finally {
            setIsLoadingGalleries(false);
        }
    };

    // Refresh gallery items
    const refreshGalleryItems = async (galleryId) => {
        try {
            // Fetch items for the specific gallery
            const items = await galleryService.getGalleryMedia(galleryId);
            setGalleryItems(items || []);

            // Also refresh the gallery list to update counts
            const updatedGalleries = await galleryService.getGalleries();
            setGalleries(updatedGalleries);

        } catch (error) {
            console.error('Error refreshing gallery items:', error);
            toast.error('Failed to refresh gallery items');
        }
    };

    // Render gallery card
    const renderGalleryCard = (gallery) => (
        <div key={gallery.id} className="gallery-card" onClick={() => handleGalleryClick(gallery)}>
            <div className="gallery-thumbnail">
                {loadingThumbnails[gallery.id] ? (
                    <div className="thumbnail-loading">
                        <FontAwesomeIcon icon={faSpinner} spin />
                    </div>
                ) : (
                    <>
                        {gallery.type?.toLowerCase() === 'image' && galleryThumbnails[gallery.id] ? (
                            <img
                                src={galleryThumbnails[gallery.id]}
                                alt={gallery.title}
                                className="thumbnail-image"
                            />
                        ) : (
                            <div className={`thumbnail-placeholder ${gallery.type?.toLowerCase()}`}>
                                <FontAwesomeIcon
                                    icon={
                                        gallery.type?.toLowerCase() === 'video' ? faVideo :
                                            gallery.type?.toLowerCase() === 'podcast' ? faPodcast :
                                                faImage
                                    }
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
            <div className="gallery-info">
                <Tippy content={gallery.description}>
                    <h3>{gallery.title}</h3>
                </Tippy>
            </div>
        </div>
    );

    // Refresh galleries handler
    const handleGalleriesRefresh = async () => {
        if (isRefreshingGalleries) return;

        setIsRefreshingGalleries(true);
        try {
            await fetchGalleries(true);
            await fetchMediaCentreCount();
            toast.success('Galleries refreshed');
        } catch (error) {
            console.error('Failed to refresh galleries:', error);
        } finally {
            setIsRefreshingGalleries(false);
        }
    };

    // Handle Media Centre item count changes
    const handleMediaCentreItemCountChange = useCallback((count) => {
        setMediaCentreItemCount(count);
    }, []);

    return (
        <div className='gallery-container'>
            <div className="content-header">
                <h2>Galleries & Media</h2>
                <p className="text-muted">Manage image, video and podcast galleries for the Members Portal</p>
            </div>

            <div className="content-toolbar">
                <div className="search-box">
                    <FontAwesomeIcon icon={faMagnifyingGlass} />
                    <input
                        type="text"
                        placeholder="Search galleries..."
                        value={gallerySearchTerm}
                        onChange={(e) => setGallerySearchTerm(e.target.value)}
                    />
                </div>

                <div className="d-flex justify-content-between align-items-center mb-3">
                    <div className="d-flex gap-2">
                        <button
                            className={`refresh-btn ${isRefreshingGalleries ? 'refreshing' : ''}`}
                            onClick={handleGalleriesRefresh}
                            disabled={isRefreshingGalleries || isLoadingGalleries}
                            title="Refresh galleries"
                        >
                            <FontAwesomeIcon
                                icon={faRotate}
                                className={isRefreshingGalleries ? 'fa-spin' : ''}
                            />
                            <span>{isRefreshingGalleries ? 'Refreshing...' : 'Refresh'}</span>
                        </button>

                        <button
                            onClick={handleNewGallery}
                            style={{
                                padding: '8px 16px',
                                borderRadius: '4px',
                                border: 'none',
                                backgroundColor: '#007bff',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px'
                            }}
                        >
                            <span>New Gallery</span>
                            <FontAwesomeIcon icon={faPlus} />
                        </button>
                    </div>
                </div>
            </div>

            <Tabs className="gallery-tabs" selectedIndex={selectedGalleryTab} onSelect={index => setSelectedGalleryTab(index)}>
                <TabList>
                    <Tab>
                        <FontAwesomeIcon icon={faImage} /> Image Galleries
                        <span className="tab-count">
                            {String(getFilteredGalleriesByType('image').length || 0)}
                        </span>
                    </Tab>
                    <Tab>
                        <FontAwesomeIcon icon={faVideo} /> Video Galleries
                        <span className="tab-count">
                            {String(getFilteredGalleriesByType('video').length || 0)}
                        </span>
                    </Tab>
                    <Tab>
                        <FontAwesomeIcon icon={faPodcast} /> Audio Galleries
                        <span className="tab-count">
                            {String(getFilteredGalleriesByType('podcast').length || 0)}
                        </span>
                    </Tab>
                    <Tab>
                        <FontAwesomeIcon icon={faBroadcastTower} /> Media Centre
                        <span className="tab-count">
                            {String(mediaCentreItemCount || 0)}
                        </span>
                    </Tab>
                </TabList>

                <TabPanel>
                    <div className="galleries-container">
                        {isLoadingGalleries ? (
                            <div className="loading-container">
                                <FontAwesomeIcon icon={faSpinner} spin className="loading-icon" />
                                <p>Loading galleries...</p>
                            </div>
                        ) : (
                            <>
                                <div className="gallery-controls">
                                    {/* ... existing controls ... */}
                                </div>
                                <div className="gallery-grid">
                                    {getFilteredGalleriesByType('image').map(gallery => renderGalleryCard(gallery))}
                                </div>
                            </>
                        )}
                    </div>
                </TabPanel>

                <TabPanel>
                    <div className="gallery-grid">
                        {getFilteredGalleriesByType('video').map(gallery => renderGalleryCard(gallery))}
                    </div>
                </TabPanel>

                <TabPanel>
                    <div className="gallery-grid">
                        {getFilteredGalleriesByType('podcast').map(gallery => renderGalleryCard(gallery))}
                    </div>
                </TabPanel>

                <TabPanel>
                    <MediaCentreManagement 
                        searchTerm={gallerySearchTerm}
                        isLoading={isLoadingGalleries}
                        onItemCountChange={handleMediaCentreItemCountChange}
                    />
                </TabPanel>
            </Tabs>

            {/* Modals */}
            {selectedGallery && (
                <GalleryDetail
                    gallery={selectedGallery}
                    onClose={handleGalleryClose}
                    onSave={handleGallerySave}
                    galleryItems={galleryItems}
                    setGalleryItems={setGalleryItems}
                    refreshGalleryItems={refreshGalleryItems}
                    refreshGalleries={fetchGalleries}
                />
            )}
            {showNewGalleryForm && (
                <NewGalleryForm
                    onClose={handleNewGalleryClose}
                    onSave={handleNewGallerySave}
                    defaultGalleryType={getCurrentGalleryType()}
                    existingGalleries={galleries}
                />
            )}
        </div>
    );
}

export default GalleryManagement; 